import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { useAuthStore } from '@avanta/shared/stores/auth';
import type { AuthInfo } from '@avanta/shared/types/auth';

type Authenticate = (username: string, password: string, magicToken?: string) => Promise<AuthInfo>;
type Login = (username: string, password: string, magicToken?: string) => Promise<void>;

export interface UseLoginOptions {
  authenticate: Authenticate;
  onSuccess: (authInfo: AuthInfo) => void | Promise<void>;
}

export interface UseLoginReturn {
  login: Login;
  isLoading: boolean;
}

export const useLogin = ({ authenticate, onSuccess }: UseLoginOptions): UseLoginReturn => {
  const { t } = useTranslation(LOCIZE_NS.COMMON);
  const setAuthInfo = useAuthStore(state => state.setAuthInfo);
  const [isLoading, setIsLoading] = useState(false);

  const login: Login = async (username, password, magicToken) => {
    setIsLoading(true);
    try {
      const authInfo = await authenticate(username, password, magicToken);
      setAuthInfo(authInfo);
      await onSuccess(authInfo);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? t('Login.error', { ns: LOCIZE_NS.AUTH }) : t('Common.error');
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return { login, isLoading };
};
